import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { CourtSystemParams } from '@ftr/routing-paths'
import { FeatureFlagKeyOfType, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationGuard, AuthenticationService } from '@ftr/ui-user'

export function makeFeatureFlagGuard(flagName: FeatureFlagKeyOfType<boolean>): CanActivateFn {
  return async route => {
    const launchDarklyService = inject(LaunchDarklyService)
    const authService = inject(AuthenticationService)
    const router = inject(Router)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    const featureFlagEnabled = await launchDarklyService.getFlagVariationForUser(
      authService.currentUser!,
      flagName,
      courtSystemId,
    )
    if (!featureFlagEnabled) {
      return router.createUrlTree(['../'])
    }
    return true
  }
}

export function makePublicFeatureFlagGuard(flagName: FeatureFlagKeyOfType<boolean>): CanActivateFn {
  return async route => {
    const launchDarklyService = inject(LaunchDarklyService)
    const router = inject(Router)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    const featureFlagEnabled = await launchDarklyService.getFlagVariationForAnonymous(flagName, courtSystemId)
    if (!featureFlagEnabled) {
      return router.createUrlTree(['../'])
    }
    return true
  }
}

/**
 * For scenarios where a feature will be public - but previously behind authentication.
 * When a flag is provided - will only auth when the flag is disabled
 */
export function authIfFeatureFlagDisabled(flagName: FeatureFlagKeyOfType<boolean>): CanActivateFn {
  return async (route, state) => {
    const launchDarklyService = inject(LaunchDarklyService)
    const authService = inject(AuthenticationService)
    const authenticationGuard = inject(AuthenticationGuard)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    // Check feature flag - supports both authenticated and anonymous users
    let featureFlagEnabled: boolean
    if (authService.currentUser) {
      featureFlagEnabled = await launchDarklyService.getFlagVariationForUser(
        authService.currentUser,
        flagName,
        courtSystemId,
      )
    } else {
      featureFlagEnabled = await launchDarklyService.getFlagVariationForAnonymous(flagName, courtSystemId)
    }

    // If feature flag is OFF (disabled), require authentication
    // If feature flag is ON (enabled), allow public access
    if (!featureFlagEnabled) {
      return authenticationGuard.canActivate(route, state)
    }

    return true
  }
}
